/**
 * Browser Notification Service
 * Handles native browser notifications for important events
 */

class BrowserNotificationService {
  private enabled = false;

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.enabled = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.enabled = permission === 'granted';
      return permission === 'granted';
    }

    return false;
  }

  isSupported(): boolean {
    return 'Notification' in window;
  }

  isEnabled(): boolean {
    return this.enabled && Notification.permission === 'granted';
  }

  getPermission(): NotificationPermission {
    return Notification.permission;
  }

  showNotification(title: string, options?: NotificationOptions): Notification | null {
    if (!this.isEnabled()) {
      console.warn('Notifications are not enabled');
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/icon-192x192.png', // You can customize this
        badge: '/badge-72x72.png',
        ...options
      });

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return null;
    }
  }

  // Convenience methods for different notification types
  showTicketCreated(ticketNumber: string, subject: string): Notification | null {
    return this.showNotification('New Ticket Created', {
      body: `${ticketNumber}: ${subject}`,
      tag: 'ticket-created',
      icon: '/icon-192x192.png'
    });
  }

  showTicketAssigned(ticketNumber: string, subject: string): Notification | null {
    return this.showNotification('Ticket Assigned to You', {
      body: `${ticketNumber}: ${subject}`,
      tag: 'ticket-assigned',
      icon: '/icon-192x192.png',
      requireInteraction: true // Keep notification visible until user interacts
    });
  }

  showNewMessage(ticketNumber: string, authorName: string, preview: string): Notification | null {
    return this.showNotification(`New Message from ${authorName}`, {
      body: `${ticketNumber}: ${preview}`,
      tag: 'new-message',
      icon: '/icon-192x192.png'
    });
  }

  showTicketStatusChanged(ticketNumber: string, newStatus: string): Notification | null {
    return this.showNotification('Ticket Status Updated', {
      body: `${ticketNumber} is now ${newStatus}`,
      tag: 'status-changed',
      icon: '/icon-192x192.png'
    });
  }

  showTicketEscalated(ticketNumber: string): Notification | null {
    return this.showNotification('Ticket Escalated', {
      body: `${ticketNumber} has been escalated to you`,
      tag: 'ticket-escalated',
      icon: '/icon-192x192.png',
      requireInteraction: true
    });
  }

  showSlaDeadlineApproaching(ticketNumber: string, minutesRemaining: number): Notification | null {
    return this.showNotification('SLA Deadline Approaching', {
      body: `${ticketNumber} - ${minutesRemaining} minutes remaining`,
      tag: 'sla-warning',
      icon: '/icon-192x192.png',
      requireInteraction: true
    });
  }
}

export const browserNotificationService = new BrowserNotificationService();
